/**
 * Dynamic Range Calculator for Care360 Simulation Sandbox
 * 
 * This utility extracts and reuses the exact same calculation logic that
 * HealthSnapshotV2 uses for ideal targets and standard deviations to ensure
 * perfect consistency between simulation and actual health scoring.
 * 
 * Key Integration Points:
 * - Uses same calculateDynamicTarget() function as HealthSnapshotV2
 * - Extracts standard deviation from same scoring configuration files
 * - Applies same 6.0 standard deviation threshold for range calculation
 * - Maintains identical parameter naming conventions (parameters.md)
 */

import { calculateDynamicTarget } from '../../utils/scoringEngineV2';

/**
 * Calculate patient-specific ideal target for a parameter
 * Uses the exact same logic as HealthSnapshotV2
 */
export const getPatientSpecificIdealTarget = (paramKey, patientDemographics, targetFormulas) => {
  console.log('[DynamicRangeCalculator] Calculating ideal target for:', {
    paramKey,
    hasPatientDemographics: !!patientDemographics,
    patientAge: patientDemographics?.age,
    patientSex: patientDemographics?.sex,
    targetFormulasCount: targetFormulas?.length
  });

  if (!patientDemographics || !targetFormulas) {
    console.warn('[DynamicRangeCalculator] Missing demographics or target formulas');
    return null;
  }

  try {
    const targetResult = calculateDynamicTarget(paramKey, patientDemographics, targetFormulas);
    
    if (targetResult && typeof targetResult.value === 'number' && !isNaN(targetResult.value)) {
      console.log('[DynamicRangeCalculator] Ideal target calculated successfully:', {
        paramKey,
        idealTarget: targetResult.value,
        formula: targetResult.finalFormula
      });
      return {
        value: targetResult.value,
        formula: targetResult.finalFormula,
        details: targetResult
      };
    } else {
      console.warn('[DynamicRangeCalculator] Invalid target calculation result:', targetResult);
      return null;
    }
  } catch (error) {
    console.error('[DynamicRangeCalculator] Error calculating ideal target:', error);
    return null;
  }
};

/**
 * Extract parameter standard deviation from scoring configuration
 * Uses the same logic as HealthSnapshotV2 for bidirectional SD handling
 */
export const getParameterStandardDeviation = (paramKey, scoringConfig) => {
  console.log('[DynamicRangeCalculator] Extracting standard deviation for:', {
    paramKey,
    scoringConfigLength: scoringConfig?.length
  });

  if (!scoringConfig || !Array.isArray(scoringConfig)) {
    console.warn('[DynamicRangeCalculator] Invalid scoring configuration');
    return null;
  }

  const paramConfig = scoringConfig.find(config => config.key === paramKey);
  if (!paramConfig) {
    console.warn('[DynamicRangeCalculator] Parameter not found in scoring config:', paramKey);
    return null;
  }

  // Extract standard deviation using HealthSnapshotV2 logic
  let sigmaLow = paramConfig.stdDeviation_low !== undefined ? parseFloat(paramConfig.stdDeviation_low) : undefined;
  let sigmaHigh = paramConfig.stdDeviation_high !== undefined ? parseFloat(paramConfig.stdDeviation_high) : undefined;
  
  // Fallback to single stdDeviation or V1's sd.value structure
  let sigmaSingle = paramConfig.stdDeviation !== undefined ? parseFloat(paramConfig.stdDeviation) : undefined;
  if (sigmaSingle === undefined && paramConfig.sd && paramConfig.sd.value !== undefined) {
    sigmaSingle = parseFloat(paramConfig.sd.value);
  }

  const result = {
    paramKey,
    sigmaLow: sigmaLow || sigmaSingle,
    sigmaHigh: sigmaHigh || sigmaSingle,
    sigmaSingle,
    isBidirectional: !!(sigmaLow && sigmaHigh),
    unit: paramConfig.sd?.unit || paramConfig.unit || 'units'
  };

  console.log('[DynamicRangeCalculator] Standard deviation extracted:', result);
  return result;
};

/**
 * Calculate dynamic slider range using HealthSnapshotV2 methodology
 * Formula: idealTarget ± (6.0 × standardDeviation) with safety bounds
 */
export const calculateDynamicSliderRange = (
  paramKey, 
  patientDemographics, 
  targetFormulas, 
  scoringConfig,
  safetyBounds = { min: 50, max: 300 }
) => {
  console.log('[DynamicRangeCalculator] Calculating dynamic range for:', {
    paramKey,
    safetyBounds
  });

  // Step 1: Get patient-specific ideal target
  const idealTargetResult = getPatientSpecificIdealTarget(paramKey, patientDemographics, targetFormulas);
  if (!idealTargetResult) {
    console.warn('[DynamicRangeCalculator] Could not calculate ideal target, using safety bounds');
    return {
      min: safetyBounds.min,
      max: safetyBounds.max,
      idealTarget: null,
      standardDeviation: null,
      calculationMethod: 'safety_bounds_fallback',
      error: 'Could not calculate ideal target'
    };
  }

  // Step 2: Get parameter standard deviation
  const sdResult = getParameterStandardDeviation(paramKey, scoringConfig);
  if (!sdResult || (!sdResult.sigmaSingle && !sdResult.sigmaLow && !sdResult.sigmaHigh)) {
    console.warn('[DynamicRangeCalculator] Could not extract standard deviation, using safety bounds');
    return {
      min: safetyBounds.min,
      max: safetyBounds.max,
      idealTarget: idealTargetResult.value,
      standardDeviation: null,
      calculationMethod: 'safety_bounds_fallback',
      error: 'Could not extract standard deviation'
    };
  }

  // Step 3: Calculate dynamic range using 15.0 SD threshold for wider simulation range
  const idealTarget = idealTargetResult.value;
  const MAX_Z_FOR_ZERO_HEALTH = 15.0; // Increased from 6.0 for wider sandbox simulation
  
  // Use appropriate standard deviation (bidirectional if available)
  const sdLow = sdResult.sigmaLow || sdResult.sigmaSingle;
  const sdHigh = sdResult.sigmaHigh || sdResult.sigmaSingle;
  
  const calculatedMin = idealTarget - (MAX_Z_FOR_ZERO_HEALTH * sdLow);
  const calculatedMax = idealTarget + (MAX_Z_FOR_ZERO_HEALTH * sdHigh);

  // Step 4: Apply safety bounds
  const finalMin = Math.max(safetyBounds.min, calculatedMin);
  const finalMax = Math.min(safetyBounds.max, calculatedMax);

  const result = {
    min: Math.round(finalMin),
    max: Math.round(finalMax),
    idealTarget: Math.round(idealTarget * 10) / 10, // Round to 1 decimal
    standardDeviation: {
      low: sdLow,
      high: sdHigh,
      isBidirectional: sdResult.isBidirectional
    },
    calculationMethod: 'dynamic_healthsnapshot_v2',
    calculationDetails: {
      rawMin: calculatedMin,
      rawMax: calculatedMax,
      appliedSafetyBounds: finalMin !== calculatedMin || finalMax !== calculatedMax,
      targetFormula: idealTargetResult.formula,
      maxZThreshold: MAX_Z_FOR_ZERO_HEALTH
    }
  };

  console.log('[DynamicRangeCalculator] Dynamic range calculated successfully:', result);
  return result;
};

/**
 * Get appropriate default value for slider initialization
 * Priority: Patient's actual value > Ideal target > Range midpoint
 */
export const getSliderDefaultValue = (paramKey, patientData, dynamicRange) => {
  console.log('[DynamicRangeCalculator] Determining default slider value for:', {
    paramKey,
    hasPatientData: !!patientData,
    dynamicRange: dynamicRange ? { min: dynamicRange.min, max: dynamicRange.max, idealTarget: dynamicRange.idealTarget } : null
  });

  // Priority 1: Patient's actual value from flowsheet
  if (patientData && typeof patientData === 'number' && !isNaN(patientData)) {
    // Ensure patient value is within calculated range
    if (dynamicRange && patientData >= dynamicRange.min && patientData <= dynamicRange.max) {
      console.log('[DynamicRangeCalculator] Using patient actual value:', patientData);
      return patientData;
    } else if (dynamicRange) {
      console.log('[DynamicRangeCalculator] Patient value outside range, using ideal target');
      return dynamicRange.idealTarget || Math.round((dynamicRange.min + dynamicRange.max) / 2);
    }
  }

  // Priority 2: Calculated ideal target
  if (dynamicRange && dynamicRange.idealTarget) {
    console.log('[DynamicRangeCalculator] Using calculated ideal target:', dynamicRange.idealTarget);
    return dynamicRange.idealTarget;
  }

  // Priority 3: Range midpoint
  if (dynamicRange) {
    const midpoint = Math.round((dynamicRange.min + dynamicRange.max) / 2);
    console.log('[DynamicRangeCalculator] Using range midpoint:', midpoint);
    return midpoint;
  }

  // Fallback: Default for vitals_sbp
  console.warn('[DynamicRangeCalculator] Using hardcoded fallback value');
  return 120; // Reasonable default for systolic BP
};

export default {
  getPatientSpecificIdealTarget,
  getParameterStandardDeviation,
  calculateDynamicSliderRange,
  getSliderDefaultValue
};
